import classNames from 'classnames';
import { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const HeaderContainer = styled(FullWidthContainer)`
  z-index: 1;
  display: flex;
  justify-content: center;
  padding-top: var(--double-unit);

  &.withRarityBackground {
    background-image: linear-gradient(
      0deg,
      var(--color) 0%,
      var(--current-background-color) 100%
    );
  }

  @media ${laptopAndAbove} {
    padding-top: 0;
  }
`;

const Grid = styled(FullWidthContainer)`
  --nb-items-max-per-row: 3;
  --gap: var(--triple-unit);
  --item-width: calc(
    (100% - (var(--nb-items-max-per-row) - 1) * var(--gap)) /
      var(--nb-items-max-per-row)
  );
  background-size: 100% clamp(50%, 600px, 95%);
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  gap: var(--gap);
  flex-wrap: wrap;

  @media ${laptopAndAbove} {
    &:not(.multiple) {
      flex-wrap: nowrap;
    }
  }

  &.withRarityBackground {
    background-image: linear-gradient(
      180deg,
      var(--color) 0%,
      rgba(var(--c-rgb-black), 0) 100%
    );
  }

  > * {
    flex-basis: 100%;

    @media ${tabletAndAbove} {
      flex-basis: clamp(350px, var(--item-width), 450px);
      min-width: 450px;
    }
  }
`;

type Props = {
  leagueSlug: string;
  withRarityBackground?: boolean;
  disableSticky?: boolean;
  leagueColor: string;
  header: ReactNode;
  multiple?: boolean;
  onIsVisibleCallback: () => void;
};
export const LeagueTeams = ({
  leagueSlug,
  withRarityBackground = true,
  disableSticky = false,
  leagueColor,
  header,
  multiple = true,
  onIsVisibleCallback,
  children,
}: PropsWithChildren<Props>) => {
  const { handleRef } =
    useIsVisibleInViewport<HTMLDivElement>(onIsVisibleCallback);

  return (
    <ContentWithStickyHeader
      disableSticky={disableSticky}
      header={
        <HeaderContainer
          className={classNames({
            withRarityBackground,
          })}
          style={
            {
              '--color': leagueColor,
            } as CSSProperties
          }
        >
          {header}
        </HeaderContainer>
      }
    >
      <Grid
        id={leagueSlug}
        className={classNames({
          multiple,
          withRarityBackground,
        })}
        style={
          {
            '--nb-items-max-per-row': multiple ? 3 : 1,
            '--color': leagueColor,
          } as CSSProperties
        }
        ref={handleRef}
      >
        {children}
      </Grid>
    </ContentWithStickyHeader>
  );
};
